import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const KEY = "kp-cookie-consent";
type Consent = { essential: true; analytics: boolean; date: string };

const read = (): Consent | null => {
  try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
};

export const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const existing = read();
    if (!existing) setVisible(true); else setAnalytics(existing.analytics);
    const open = () => { const c = read(); setAnalytics(c?.analytics ?? false); setSettings(true); setVisible(true); };
    window.addEventListener("kp:open-cookie-settings", open);
    return () => window.removeEventListener("kp:open-cookie-settings", open);
  }, []);

  const save = (a: boolean) => {
    const consent: Consent = { essential: true, analytics: a, date: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(consent));
    setVisible(false); setSettings(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div role="dialog" aria-modal="false" aria-labelledby="cookie-title" className="fixed inset-x-4 bottom-4 z-[60] sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} data-testid="cookie-banner">
          <div className="card p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="rounded-full bg-clay-100 p-2 text-clay"><Cookie className="h-5 w-5" aria-hidden /></span>
              <div>
                <h2 id="cookie-title" className="font-display text-lg font-semibold">{t.cookie.title}</h2>
                <p className="mt-1.5 text-sm text-charcoal-500 leading-relaxed">{t.cookie.text} <Link to="/datenschutz" className="underline underline-offset-2 hover:text-clay" data-testid="cookie-privacy-link">{t.cookie.more}</Link></p>
              </div>
            </div>

            {settings && (
              <div className="mt-5 grid gap-3" data-testid="cookie-settings-panel">
                <label className="flex items-start justify-between gap-4 rounded-2xl bg-sand-50 p-4">
                  <span><span className="block text-sm font-semibold">{t.cookie.essential}</span><span className="block text-xs text-charcoal-500 mt-0.5">{t.cookie.essentialText}</span></span>
                  <input type="checkbox" checked disabled className="mt-1 h-4 w-4 accent-clay" aria-label={t.cookie.essential} data-testid="cookie-essential-toggle" />
                </label>
                <label className="flex items-start justify-between gap-4 rounded-2xl bg-sand-50 p-4 cursor-pointer">
                  <span><span className="block text-sm font-semibold">{t.cookie.analytics}</span><span className="block text-xs text-charcoal-500 mt-0.5">{t.cookie.analyticsText}</span></span>
                  <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-1 h-4 w-4 accent-clay" aria-label={t.cookie.analytics} data-testid="cookie-analytics-toggle" />
                </label>
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" className="btn-primary !py-2.5 !px-5" onClick={() => save(true)} data-testid="cookie-accept-all">{t.cookie.acceptAll}</button>
              <button type="button" className="btn-outline !py-2.5 !px-5" onClick={() => save(false)} data-testid="cookie-essential-only">{t.cookie.essentialOnly}</button>
              {settings ? (
                <button type="button" className="btn-ghost !py-2.5 !px-5" onClick={() => save(analytics)} data-testid="cookie-save">{t.cookie.save}</button>
              ) : (
                <button type="button" className="btn-ghost !py-2.5 !px-5" onClick={() => setSettings(true)} data-testid="cookie-open-settings">{t.cookie.settings}</button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
